/**
 * Phase 17 — Bulk Invite Users dialog.
 *
 * Three-phase modal:
 *   1. Upload CSV (with sample-csv link + on-conflict choice)
 *   2. Dry-run preview (counts + errors + conflict resolution)
 *   3. Confirm + commit → success modal showing the temp-password table
 *      with copy-all + download-CSV affordances and an "I have stored
 *      these securely" acknowledgement before the dialog closes.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  Loader2,
  ShieldCheck,
  Copy,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BulkInviteUsersDialog({ open, onOpenChange, onComplete }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmToken, setConfirmToken] = useState(null);
  const [confirmTokenTtl, setConfirmTokenTtl] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [onConflict, setOnConflict] = useState("skip");
  const [result, setResult] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview(null);
      setConfirmToken(null);
      setConfirmTokenTtl(0);
      setResult(null);
      setAcknowledged(false);
      setOnConflict("skip");
    } else {
      api.get("/admin/users/bulk-invite/history")
        .then((r) => setHistory(r.data || []))
        .catch(() => setHistory([]));
    }
  }, [open]);

  const handleChooseFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setConfirmToken(null);
    setResult(null);
  };

  const runDryRun = async () => {
    if (!file) return;
    setPreviewing(true);
    const fd = new FormData();
    fd.append("csv", file);
    fd.append("dry_run", "true");
    fd.append("on_conflict", onConflict);
    try {
      const { data } = await api.post("/admin/users/bulk-invite", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setConfirmToken(data.confirm_token || null);
      setConfirmTokenTtl(data.confirm_token_ttl_sec || 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!file) return;
    if (!confirmToken) {
      toast.error("Confirm token missing — run a fresh dry-run.");
      return;
    }
    setCommitting(true);
    const fd = new FormData();
    fd.append("csv", file);
    fd.append("dry_run", "false");
    fd.append("on_conflict", onConflict);
    fd.append("confirm_token", confirmToken);
    try {
      const { data } = await api.post("/admin/users/bulk-invite", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setPreview(null);
      setConfirmToken(null);
      onComplete?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCommitting(false);
    }
  };

  const downloadSampleCsv = async () => {
    try {
      const { data } = await api.get("/admin/users/bulk-invite/sample.csv", {
        responseType: "text",
      });
      const blob = new Blob([data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "users_bulk_invite_sample.csv";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 200);
    } catch {
      toast.error("Couldn't fetch sample CSV.");
    }
  };

  const downloadCredentialsCsv = () => {
    const all = [...(result?.created_users || []), ...(result?.updated_users || [])];
    if (!all.length) return;
    const header = "email,full_name,role,is_admin,service_line,temp_password\n";
    const rows = all
      .map((u) =>
        [
          u.email,
          `"${(u.full_name || "").replace(/"/g, '""')}"`,
          u.role,
          u.is_admin ? "true" : "false",
          u.service_line || "",
          u.temp_password,
        ].join(","),
      )
      .join("\n");
    const blob = new Blob([header + rows + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `goodwin-resolve-credentials-${result.batch_id}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 200);
  };

  const copyAllCredentials = async () => {
    const all = [...(result?.created_users || []), ...(result?.updated_users || [])];
    if (!all.length) return;
    const text = all
      .map(
        (u) =>
          `${u.email}\t${u.full_name}\t${u.role}\t${u.is_admin ? "admin" : ""}\t${u.temp_password}`,
      )
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Credentials copied (tab-delimited)");
    } catch {
      toast.error("Couldn't copy — please use Download CSV.");
    }
  };

  const allCreated = [...(result?.created_users || []), ...(result?.updated_users || [])];

  return (
    <Dialog open={open} onOpenChange={(v) => !committing && onOpenChange(v)}>
      <DialogContent
        className="max-w-3xl"
        data-testid="bulk-invite-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" style={{ color: "#5EAB45" }} />
              Bulk invite users
            </span>
          </DialogTitle>
          <DialogDescription>
            Upload a CSV. Each row becomes an active user with a strong
            temporary password. Welcome emails are queued in the Email
            Outbox so you can hand-deliver them while real email is mocked.
          </DialogDescription>
        </DialogHeader>

        {/* ---------- Step 1: choose file ---------- */}
        {!preview && !result && (
          <div className="space-y-4">
            <div className="rounded-lg border border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-5 text-center">
              <Upload className="mx-auto mb-2 h-6 w-6 text-[color:var(--page-fg-muted)]" strokeWidth={1.5} />
              <p className="text-sm text-[color:var(--page-fg-secondary)]">
                {file ? (
                  <>Selected: <span className="font-mono">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB</>
                ) : (
                  <>Drop a CSV here or click below to choose one. Max 500 rows, 1 MB.</>
                )}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleChooseFile}
                className="hidden"
                data-testid="bulk-invite-file-input"
              />
              <div className="mt-3 flex justify-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="bulk-invite-choose-file"
                >
                  Choose CSV
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadSampleCsv}
                  data-testid="bulk-invite-sample-csv"
                >
                  <Download className="mr-1 h-3.5 w-3.5" />
                  Download sample CSV
                </Button>
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.10em] text-[color:var(--page-fg-muted)]">
                If an email already exists in the system
              </p>
              <RadioGroup
                value={onConflict}
                onValueChange={setOnConflict}
                className="flex gap-4"
                data-testid="bulk-invite-on-conflict"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="skip" id="oc-skip" />
                  <Label htmlFor="oc-skip" className="cursor-pointer text-sm">
                    Skip the row
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="update" id="oc-update" />
                  <Label htmlFor="oc-update" className="cursor-pointer text-sm">
                    Update existing user (rotates their password)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {history.length > 0 && (
              <div className="rounded-lg border border-[color:var(--border-subtle)] p-3">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--page-fg-muted)]">
                  Recent imports
                </p>
                <ul className="space-y-1 text-xs">
                  {history.slice(0, 5).map((h) => (
                    <li key={h.id} className="flex justify-between gap-2 text-[color:var(--page-fg-secondary)]">
                      <span className="truncate">
                        <span className="font-mono">{h.batch_id || h.id?.slice(0, 8)}</span> ·{" "}
                        {h.filename || "(unnamed)"}
                      </span>
                      <span className="tabular-nums">
                        +{h.rows_created || 0} new · ↻{h.rows_updated || 0} · ⊘{h.rows_failed || 0}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ---------- Step 2: preview ---------- */}
        {preview && !result && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-4">
              <PreviewStat label="Rows in CSV" value={preview.total_rows} />
              <PreviewStat label="Will create" value={preview.would_create} accent="#3F7E2D" />
              <PreviewStat label="Will update / skip" value={preview.would_update + preview.would_skip} accent="var(--page-fg-secondary)" />
              <PreviewStat label="Errors" value={preview.errors?.length || 0} accent="var(--danger-fg)" />
            </div>
            {confirmToken && confirmTokenTtl > 0 && (
              <p
                className="text-xs"
                style={{ color: "var(--page-fg-muted)" }}
                data-testid="bulk-invite-confirm-token-ttl"
              >
                Confirm token issued · expires in {Math.round(confirmTokenTtl / 60)} min ·
                bound to this CSV's sha256.
              </p>
            )}
            {preview.errors?.length > 0 && (
              <div className="rounded-lg border border-[color:var(--danger-border,rgba(220,38,38,0.30))] p-3" style={{ backgroundColor: "var(--danger-bg,rgba(220,38,38,0.06))" }}>
                <p className="mb-1 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--danger-fg)" }}>
                  <AlertCircle className="h-3.5 w-3.5" />
                  {preview.errors.length} row{preview.errors.length === 1 ? "" : "s"} will be skipped due to errors
                </p>
                <ul className="ml-4 list-disc space-y-0.5 text-xs text-[color:var(--page-fg-secondary)]">
                  {preview.errors.slice(0, 8).map((e, i) => (
                    <li key={i}>
                      Row {e.row}: {e.reason}
                    </li>
                  ))}
                  {preview.errors.length > 8 && <li>(+{preview.errors.length - 8} more)</li>}
                </ul>
              </div>
            )}
            {preview.conflicts_preview?.length > 0 && (
              <div className="rounded-lg border border-[color:var(--border-subtle)] p-3">
                <p className="mb-1 text-xs font-semibold text-[color:var(--page-fg)]">
                  {preview.conflicts_preview.length} email
                  {preview.conflicts_preview.length === 1 ? "" : "s"} already exist
                  {onConflict === "skip" ? " (will be skipped)" : " (will be updated)"}
                </p>
                <ul className="ml-4 list-disc space-y-0.5 text-xs text-[color:var(--page-fg-secondary)]">
                  {preview.conflicts_preview.slice(0, 8).map((c, i) => (
                    <li key={i}>
                      Row {c.row}: <span className="font-mono">{c.email}</span> · existing role={c.existing_role || "—"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ---------- Step 3: success ---------- */}
        {result && (
          <div className="space-y-3">
            <div
              className="flex items-start gap-3 rounded-lg p-4"
              style={{
                backgroundColor: "rgba(94,171,69,0.08)",
                border: "1px solid rgba(94,171,69,0.30)",
              }}
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: "#3F7E2D" }} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[color:var(--page-fg)]">
                  Import complete · batch {result.batch_id}
                </p>
                <p className="text-xs text-[color:var(--page-fg-secondary)]">
                  {result.rows_created} created · {result.rows_updated} updated ·{" "}
                  {result.rows_skipped} skipped · {result.outbox_enqueued} welcome email
                  {result.outbox_enqueued === 1 ? "" : "s"} queued in the Email Outbox.
                </p>
              </div>
            </div>

            {allCreated.length > 0 && (
              <div className="rounded-lg border border-[color:var(--border-subtle)]">
                <div
                  className="flex items-center justify-between px-3 py-2"
                  style={{ backgroundColor: "var(--warning-bg)", borderBottom: "1px solid var(--warning-border)" }}
                >
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--warning-fg)" }}>
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Show the temp passwords ONCE — they are not stored in readable form anywhere.
                  </span>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={copyAllCredentials} data-testid="bulk-invite-copy-all">
                      <Copy className="mr-1 h-3.5 w-3.5" />
                      Copy all
                    </Button>
                    <Button size="sm" variant="outline" onClick={downloadCredentialsCsv} data-testid="bulk-invite-download-csv">
                      <Download className="mr-1 h-3.5 w-3.5" />
                      Download CSV
                    </Button>
                  </div>
                </div>
                <div className="max-h-[280px] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[color:var(--surface-2)] text-left text-[10px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                      <tr>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Role</th>
                        <th className="px-3 py-2">Temp password</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[color:var(--border-subtle)]">
                      {allCreated.map((u) => (
                        <tr key={u.id} data-testid={`bulk-invite-cred-row-${u.id}`}>
                          <td className="px-3 py-2 font-mono">{u.email}</td>
                          <td className="px-3 py-2">{u.full_name}</td>
                          <td className="px-3 py-2 text-[color:var(--page-fg-secondary)]">
                            {u.role}{u.is_admin ? " · admin" : ""}
                          </td>
                          <td className="px-3 py-2 font-mono font-semibold" style={{ color: "var(--page-fg)" }}>
                            {u.temp_password}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm" data-testid="bulk-invite-ack-label">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                data-testid="bulk-invite-ack"
                className="h-4 w-4"
              />
              I have stored these temporary passwords securely. (Required to close.)
            </label>
          </div>
        )}

        <DialogFooter>
          {/* Step 1 footer */}
          {!preview && !result && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={runDryRun}
                disabled={!file || previewing}
                data-testid="bulk-invite-preview"
                className="goodwin-primary-btn text-white"
              >
                {previewing ? (
                  <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Validating…</>
                ) : (
                  <>Preview import</>
                )}
              </Button>
            </>
          )}
          {/* Step 2 footer */}
          {preview && !result && (
            <>
              <Button
                variant="outline"
                onClick={() => setPreview(null)}
                data-testid="bulk-invite-back"
                disabled={committing}
              >
                Back
              </Button>
              <Button
                onClick={commit}
                disabled={committing || preview.would_create + preview.would_update === 0}
                data-testid="bulk-invite-commit"
                className="goodwin-primary-btn text-white"
              >
                {committing ? (
                  <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Importing…</>
                ) : (
                  <>Import {preview.would_create + preview.would_update} user{(preview.would_create + preview.would_update) === 1 ? "" : "s"}</>
                )}
              </Button>
            </>
          )}
          {/* Step 3 footer */}
          {result && (
            <Button
              disabled={!acknowledged}
              onClick={() => onOpenChange(false)}
              data-testid="bulk-invite-done"
              className="goodwin-primary-btn text-white"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewStat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--page-fg-muted)]">
        {label}
      </p>
      <p
        className="mt-1 text-2xl font-semibold tabular-nums"
        style={{ color: accent || "var(--page-fg)" }}
      >
        {value || 0}
      </p>
    </div>
  );
}
